import React from 'react';
import type { AdminUserDetailUserInfo } from './adminUserDetailTypes';

interface AdminUserDetailDeleteUserModalProps {
  show: boolean;
  userInfo: AdminUserDetailUserInfo;
  deleteUserConfirmValue: string;
  setDeleteUserConfirmValue: (v: string) => void;
  deletingUser: boolean;
  deletionStats: any;
  setDeletionStats: (v: any) => void;
  onClose: () => void;
  onConfirm: () => void;
}

const AdminUserDetailDeleteUserModal: React.FC<AdminUserDetailDeleteUserModalProps> = ({
  show,
  userInfo,
  deleteUserConfirmValue,
  setDeleteUserConfirmValue,
  deletingUser,
  deletionStats,
  setDeletionStats,
  onClose,
  onConfirm,
}) => {
  if (!show) return null;

  const handleClose = () => {
    onClose();
    setDeleteUserConfirmValue('');
    setDeletionStats(null);
  };

  const canConfirm = deleteUserConfirmValue.trim().toLowerCase() === userInfo.email.toLowerCase() && !deletingUser;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-slate-900/90 border border-white/10 rounded-xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Delete User</h3>
            <p className="text-sm text-gray-300">This action cannot be undone. Type the user's email to confirm.</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white" aria-label="Close delete dialog" disabled={deletingUser}>
            ✕
          </button>
        </div>
        <div className="space-y-4">
          <div className="bg-slate-800/60 border border-white/10 rounded-lg p-4">
            <p className="text-sm text-gray-300">User</p>
            <p className="text-base font-semibold text-white">{userInfo.name}</p>
            <p className="text-sm text-gray-400">{userInfo.email}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">
              Type <span className="text-white font-semibold">{userInfo.email}</span> to confirm
            </label>
            <input
              type="text"
              value={deleteUserConfirmValue}
              onChange={(e) => setDeleteUserConfirmValue(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="user@example.com"
              autoFocus
              disabled={deletingUser}
            />
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-100">
            <p className="font-semibold mb-2 text-red-200">The following data will be permanently removed:</p>
            <ul className="list-disc list-inside space-y-1 text-red-100">
              <li>All runs and leads</li>
              <li>Inbound configurations, logs, and matching rules</li>
              <li>Assistant configuration</li>
              <li>Credit transactions and reserves</li>
            </ul>
          </div>
          {deletionStats && (
            <div className="bg-slate-800/60 border border-white/10 rounded-lg p-4">
              <p className="text-sm font-semibold text-white mb-2">Deletion Summary:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(deletionStats)
                  .filter(([, value]) => (value as number) > 0)
                  .map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-400">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}:
                      </span>
                      <span className="text-white font-semibold">{value as number}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleClose}
            className="flex-1 bg-white/5 text-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors border border-white/10"
            disabled={deletingUser}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center ${
              canConfirm ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-900 text-red-200 cursor-not-allowed'
            }`}
          >
            {deletingUser ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white/40 border-t-transparent rounded-full mr-2"></span>
                Deleting…
              </>
            ) : (
              'Delete User'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetailDeleteUserModal;
